import React from 'react';
import { Trans } from 'react-i18next';
import { useTranslation } from '~/dependencies';
import { ParticipantTypeEnum } from '~/enums/conversations';
import { SmsTemplate } from '~/types/accountInfo';
import { Container, Card, Bold, List, Line } from './styles';

import { ValidationErrors } from '../RecipientsSelector/utils';

type Props = {
  recipients: { participantType: any; mobile: string }[];
  template: SmsTemplate | null;
  maxRecipients: number;
  invalidRecipients: {
    id: number;
    participantType: ParticipantTypeEnum;
    mobile: string;
    metadata: {
      errorCode: string;
      error: string | null;
      warning: string | null;
      info: any;
    };
  }[];
};

const isEmptyContactGroup = ({ metadata }: any) =>
  metadata?.errorCode === ValidationErrors.EMPTY_CONTACT_GROUP;

export default function ConversationWarnings({
  recipients,
  maxRecipients,
  invalidRecipients,
  template,
}: Props) {
  const { t } = useTranslation();

  // eslint-disable-next-line func-names
  const render = function*() {
    const hasInvalidPhoneNumbers = invalidRecipients?.some(
      ({ metadata }) =>
        metadata?.errorCode === ValidationErrors.INVALID_PHONE_NUMBER
    );

    const hasEmptyContactGroups = invalidRecipients?.some(
      ({ metadata }) =>
        metadata?.errorCode === ValidationErrors.EMPTY_CONTACT_GROUP
    );

    const is10DLCTemplateRequired = invalidRecipients?.some(
      ({ metadata }) =>
        metadata?.errorCode === ValidationErrors.TEN_DLC_TEMPLATE_REQUIRED
    );

    const isComRegApprovedSenderRequired = invalidRecipients?.some(
      ({ metadata }) =>
        metadata?.errorCode === ValidationErrors.COMREG_APPROVED_SENDER_REQUIRED
    );

    const hasReechedMaxRecipients = recipients.length >= maxRecipients;
    const is10DLCTemplateSelected = template?.usage === '10dlc';

    if (hasInvalidPhoneNumbers || hasEmptyContactGroups) {
      /**
       * Show warnings based on contact groups and phone numbers
       */
      yield (
        <>
          {hasInvalidPhoneNumbers && (
            <span data-test-id="warning-valid-number-required">
              {t('screens.conversation.warnings.hasInvalidPhoneNumbers')}
            </span>
          )}

          {invalidRecipients
            .filter(isEmptyContactGroup)
            .map(({ groupId, groupName }: any) => (
              <span key={groupId}>
                {t('screens.conversation.warnings.isEmptyContactGroup', {
                  groupName,
                })}
              </span>
            ))}
        </>
      );
    }

    /**
     * Show warnings based on max selected recipients
     */
    if (hasReechedMaxRecipients) {
      yield (
        <span>
          {t('screens.conversation.warnings.hasReachedMaxRecipients', {
            maxRecipients,
          })}
        </span>
      );
    }

    /**
     * 10DLC and Toll-Free Template selected warnings
     */
    if (is10DLCTemplateSelected) {
      yield (
        <Card>
          <Bold>
            <Trans i18nKey="screens.conversation.warnings.is10DLCTemplateSelected.title" />
          </Bold>
          <List>
            <Line>
              <Trans
                i18nKey="screens.conversation.warnings.is10DLCTemplateSelected.lineOne"
                components={{ b: <strong /> }}
              />
            </Line>
            <Line>
              <Trans
                i18nKey="screens.conversation.warnings.is10DLCTemplateSelected.lineTwo"
                components={{ b: <strong /> }}
              />
            </Line>
            <Line>
              <Trans
                i18nKey="screens.conversation.warnings.is10DLCTemplateSelected.lineThree"
                components={{ b: <strong /> }}
              />
            </Line>
          </List>
        </Card>
      );
    }

    /**
     * 10DLC and Toll-Free Template not selected warnings
     */

    if (is10DLCTemplateRequired) {
      yield (
        <Card data-test-id="warning-10dlc-template-required">
          {t('screens.conversation.warnings.is10DLCTemplateRequired')}
        </Card>
      );
    }

    /**
     * Check if sender is ComReg approved
     */
    if (isComRegApprovedSenderRequired) {
      yield (
        <Card data-test-id="warning-approved-sender-required">
          {t('screens.conversation.warnings.isComRegApprovedSenderRequired')}
        </Card>
      );
    }

    yield null;
  };

  return (
    <Container className="conversation-warnings">
      {Array.from(render())}
    </Container>
  );
}
